/** X — marks veer perpendicular to your motion, scaled by cursor speed. Wild forms. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { PathSegment, Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 5l14 14"/><path d="M19 5L5 19"/></svg>';

export function createXShapes(): CreateModule {
  let start: Point = { x: 0, y: 0 };
  let moves: { angle: number; speed: number }[] = [];
  let lastCursor: Point | null = null;
  let speeds: number[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'xshapes',
      kind: 'create',
      name: 'X',
      apiVersion: 1,
      description: 'Wild marks that veer perpendicular to your motion, scaled by speed.',
      icon: ICON,
      params: [{ kind: 'slider', key: 'distance', label: 'Distance', min: 0, max: 100, step: 1, default: 50 }],
    },

    onPointerDown(s) {
      start = { x: s.x, y: s.y };
      lastCursor = { x: s.x, y: s.y };
      moves = [];
      speeds = [];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const cur = { x: s.x, y: s.y };
      if (!lastCursor) {
        lastCursor = cur;
        return;
      }
      const dx = cur.x - lastCursor.x;
      const dy = cur.y - lastCursor.y;
      if (Math.hypot(dx, dy) < 1.5) {
        lastCursor = cur;
        return;
      }
      speeds.push(s.speed);
      if (speeds.length > 10) speeds.shift();
      const avg = speeds.reduce((a, b) => a + b, 0) / speeds.length;
      moves.push({ angle: Math.atan2(dy, dx) - Math.PI / 2, speed: avg });
      lastCursor = cur;
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (moves.length === 0) return [];
      const multiplier = cfgNum(ctx.config, 'distance', 50) / 2;
      const segs: PathSegment[] = [{ t: 'M', p: { ...start } }];
      let p = { ...start };
      moves.forEach((m, i) => {
        const d = m.speed * multiplier * Math.min(2, 1 + i * 0.01); // clamp growth so long strokes don't fly off
        p = { x: p.x + Math.cos(m.angle) * d, y: p.y + Math.sin(m.angle) * d };
        segs.push({ t: 'L', p: { ...p } });
      });
      const style = defaultStyle();
      style.color = { ...ctx.ink };
      style.lineWidth = 1.5;
      return [ctx.newShape({ path: { segments: segs }, style })];
    },

    onClear() {
      moves = [];
      speeds = [];
      lastCursor = null;
    },

    onCommit() {
      moves = [];
      speeds = [];
      lastCursor = null;
    },
  };
}
